import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiClock, FiCalendar, FiLink, FiCopy, FiCheck } from 'react-icons/fi';
import { formatDateForDisplay, formatTimeRange } from '../../utils/datetime-comprehensive';
import QRCodeGenerator from '../common/QRCodeGenerator';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const ModalContent = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    max-width: 100%;
    max-height: 95vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--background-secondary);
    color: var(--text-primary);
  }
`;

const SessionInfo = styled.div`
  background-color: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1rem;
  margin-bottom: 1.5rem;
`;

const SessionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
`;

const SessionDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
  
  svg {
    color: var(--primary-color);
  }
`;

const TimeWindowInfo = styled.div`
  background-color: rgba(96, 165, 250, 0.1);
  border: 1px solid var(--info-color);
  border-radius: 0;
  padding: 1rem;
  margin-bottom: 1.5rem;
`;

const TimeWindowTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--info-color);
  margin-bottom: 0.5rem;
  
  svg {
    color: var(--info-color);
  }
`;

const TimeWindowText = styled.div`
  color: var(--info-color);
  font-size: 0.875rem;
`;

const Instructions = styled.div`
  background-color: rgba(104, 211, 145, 0.1);
  border: 1px solid var(--success-color);
  border-radius: 0;
  padding: 1rem;
  margin-top: 1.5rem;
`;

const InstructionsTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--success-dark);
  margin-bottom: 0.5rem;
`;

const InstructionsText = styled.div`
  color: var(--success-dark);
  font-size: 0.875rem;
  line-height: 1.4;
`;

const ShareableLinkSection = styled.div`
  background-color: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1rem;
  margin: 1.5rem 0;
`;

const ShareableLinkTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.75rem;
  
  svg {
    color: var(--info-color);
  }
`;

const LinkContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const LinkInput = styled.input`
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  background-color: var(--background-primary);
  color: var(--text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--info-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--background-secondary);
    border-color: var(--info-color);
    color: var(--info-color);
  }
  
  &.copied {
    background-color: rgba(104, 211, 145, 0.1);
    border-color: var(--success-color);
    color: var(--success-dark);
  }
`;

const LinkDescription = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.5rem;
  line-height: 1.4;
`;

function QRCodeModal({ isOpen, onClose, session }) {
  const [copied, setCopied] = useState(false);
  
  if (!session) return null;

  const qrData = `${window.location.origin}/portal/attendance/checkin/${session.id}`;
  const shareableLink = qrData;

  
  const getTimeWindowInfo = () => {
    // Use check-in times if available, otherwise fall back to session times
    const startTime = session.checkin_start_time || session.start_time;
    const endTime = session.checkin_end_time || session.end_time;
    
    if (startTime && endTime) {
      return {
        hasRestrictions: true,
        text: `Check-in Window: ${formatTimeRange(startTime, endTime)}`
      };
    }
    return {
      hasRestrictions: false,
      text: 'No time restrictions'
    };
  };

  const timeWindow = getTimeWindowInfo();

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareableLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareableLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>Attendance QR Code</ModalTitle>
              <CloseButton onClick={onClose}>
                <FiX size={20} />
              </CloseButton>
            </ModalHeader>

            <SessionInfo>
              <SessionTitle>{session.title || session.course_name}</SessionTitle>
              <SessionDetails>
                <DetailItem>
                  <FiCalendar size={16} />
                {formatDateForDisplay(session.date, 'M/d/yyyy')}
                </DetailItem>
                {session.start_time && session.end_time && (
                  <DetailItem>
                    <FiClock size={16} />
                    {formatTimeRange(session.start_time, session.end_time)}
                  </DetailItem>
                )}
              </SessionDetails>
            </SessionInfo>

            <TimeWindowInfo>
              <TimeWindowTitle>
                <FiClock size={16} />
                Time Window
              </TimeWindowTitle>
              <TimeWindowText>
                {timeWindow.hasRestrictions ? timeWindow.text : 'No time restrictions'}
              </TimeWindowText>
            </TimeWindowInfo>

            <QRCodeGenerator
              data={qrData}
              title=""
              description=""
              size={250}
              showData={false}
              showActions={true}
            />

            <ShareableLinkSection>
              <ShareableLinkTitle>
                <FiLink size={16} />
                Shareable Link
              </ShareableLinkTitle>
              <LinkContainer>
                <LinkInput
                  type="text"
                  value={shareableLink}
                  readOnly
                  onClick={(e) => e.target.select()}
                />
                <CopyButton
                  onClick={handleCopyLink}
                  className={copied ? 'copied' : ''}
                >
                  {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
                  {copied ? 'Copied!' : 'Copy'}
                </CopyButton>
              </LinkContainer>
              <LinkDescription>
                Share this link with students who cannot scan the QR code. They can click the link to check in directly.
              </LinkDescription>
            </ShareableLinkSection>

            <Instructions>
              <InstructionsTitle>
                📱 Instructions
              </InstructionsTitle>
              <InstructionsText>
                Students can scan this QR code or use the shareable link above to check in for attendance.
                {timeWindow.hasRestrictions && ' Make sure they check in during the allowed time window.'}
              </InstructionsText>
            </Instructions>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
}

export default QRCodeModal;
